import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { SelectItem } from '@/components/SingleItemSelector'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { type Skill } from '@/features/iam/skills/Skill'

/**
 * The skill selection of a form view model.
 */
export interface SkillsSelection {
  readonly availableSkills: Skill[]
  readonly skills: Skill[]
  setSkills(skills: Skill[]): void
}

export interface SkillsSelectorContainerProps {
  vm: SkillsSelection
  isDisabled?: boolean
  testId?: string
}

export const SkillsSelectorContainer = observer(
  function SkillsSelectorContainer({
    vm,
    isDisabled,
    testId,
  }: SkillsSelectorContainerProps) {
    const itemToString = (item: Skill | undefined) => item?.name || ''

    const itemSource = useMemo(
      () =>
        ItemSource.static(vm.availableSkills, (item, text) =>
          item.name.toLowerCase().includes(text.toLowerCase()),
        ),
      [vm.availableSkills],
    )

    const itemProps = useCustomSelectStateFromItemSource({
      itemSource,
    })

    return (
      <CustomSelect
        {...itemProps}
        itemToString={itemToString}
        value={vm.skills}
        onChange={vm.setSkills}
        isDisabled={isDisabled}
        multi={true}
        persist
        placeholder="Select skills"
        testId={testId}
        renderSearchItem={({ item, ...itemProps }) => (
          <SelectItem key={item.id} {...itemProps}>
            {itemToString(item)}
          </SelectItem>
        )}
      />
    )
  },
)
